import { useMemo, useRef } from 'react'
import { LuTag } from 'react-icons/lu'

import { useTagsStore } from '@/feature/tags/store'

import { TABLE_MAX_VISIBLE_TAGS } from '../TableHeaderTags/TableHeaderTags.constants'
import { ModalOpenVariant } from '../TableHeaderTags/TableHeaderTags.types'
import { TagsList } from '../TagsList/TagsList'
import styles from './TableCellTags.module.css'
import { TableCellTagsProps } from './TableCellTags.types'

export const TableCellTags = ({ assetData, maxTagsRenderCount }: TableCellTagsProps) => {
  const { openModal, togglePopout, isPopoutOpen, activeAssetData, globalOrgTags } = useTagsStore()
  const tagIconRef = useRef<HTMLDivElement>(null)

  // Create updated asset data with latest tag information from global store
  const updatedAssetData = useMemo(() => {
    if (!assetData.tags || assetData.tags.length === 0) {
      return assetData
    }

    // Update tags with latest information from global store and filter out deleted tags
    const updatedTags = assetData.tags
      .map((assetTag) => {
        // Find the corresponding tag in the global store
        const globalTag = globalOrgTags.find((globalTag) => globalTag.id === assetTag.id)

        // If tag exists in global store, update its information (name/color changes)
        if (globalTag) {
          return { ...assetTag, name: globalTag.name, color: globalTag.color }
        }

        // If tag doesn't exist in global store, it was deleted - return null to filter out
        return null
      })
      .filter((tag) => tag !== null) // Remove deleted tags (null values)

    return { ...assetData, tags: updatedTags }
  }, [assetData, globalOrgTags])

  const handleTagIconClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (tagIconRef.current) {
      const rect = tagIconRef.current.getBoundingClientRect()
      // Position directly below the icon (top-left corner aligned)
      const x = rect.left
      const y = rect.bottom

      // Use togglePopout to open/close the popout for this asset
      togglePopout({
        position: { x, y },
        activeAssetData: updatedAssetData,
      })
    }
  }

  // Determine if this tag icon should be active based on the asset ID
  const isActive = isPopoutOpen && activeAssetData?.id === updatedAssetData.id

  return (
    <div className={styles.tagsCell}>
      <div>
        <div
          ref={tagIconRef}
          onClick={handleTagIconClick}
          className={[styles.tagsCellIcon, isActive ? styles.tagsCellIconActive : ''].join(' ')}
          data-asset-id={updatedAssetData.id}
          data-testid="table-cell-tag-icon"
        >
          <LuTag size={16} />
        </div>
      </div>
      <div>
        <TagsList
          maxTagsRenderCount={maxTagsRenderCount || TABLE_MAX_VISIBLE_TAGS}
          openModal={openModal}
          modalVariant={ModalOpenVariant.ASSET}
          tagIconRef={tagIconRef}
          assetData={updatedAssetData}
          tagsList={updatedAssetData.tags}
        />
      </div>
    </div>
  )
}
